import {
  GetBusinessInfoResponse,
  MyBusiness,
} from "@1games-ru/region-shared/dist/backend/requests/business";
import { useCallback, useLayoutEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import formatPrice from "@/core/helpers/formatPrice";
import { handleNumericChange } from "@/core/helpers/handleNumericChange";
import useAppSelector from "@/core/helpers/useAppSelector";
import { Button, Icon, TextInput } from "@/shared/ui";
import DeviceTabContentWrapper from "@/shared/widgets/DeviceTabContentWrapper";
import { safeBalanceInfoSelector } from "@/domains/business/slice";
import useGetBalance from "@/domains/business/hooks/useGetBalance";
import { useSafeHandlers } from "@/domains/business/hooks/useSafeHandlers";
import BusinessProgressBar from "@/domains/business/components/BusinessProgressBar";

type Props = {
  info: MyBusiness | GetBusinessInfoResponse;
  onClose: () => void;
};

export default function BusinessSafeContainer({ info, onClose }: Props) {
  const { t } = useTranslation("business");

  const [inputValue, setInputValue] = useState("");

  const safeBalanceInfo = useAppSelector(safeBalanceInfoSelector);

  const { getBalance } = useGetBalance();
  const { isPending, takeFromSafe } = useSafeHandlers();

  const progress = safeBalanceInfo
    ? Math.round(((safeBalanceInfo.balance * 100) / safeBalanceInfo.balanceLimit) * 10) / 10
    : 0;

  const transferMoney = useCallback(() => {
    if (safeBalanceInfo) {
      setInputValue("");
      takeFromSafe(safeBalanceInfo.businessId, Number(inputValue));
    }
  }, [inputValue, safeBalanceInfo, takeFromSafe]);

  const transferAllMoney = useCallback(() => {
    if (safeBalanceInfo && safeBalanceInfo?.balance > 0) {
      takeFromSafe(safeBalanceInfo.businessId, safeBalanceInfo.balance);
    }
  }, [safeBalanceInfo, takeFromSafe]);

  const handleInputChange = (value: string) => handleNumericChange(value, setInputValue);

  useLayoutEffect(() => {
    if (info) {
      getBalance(info.id);
    }
  }, [getBalance, info]);

  const header = t("business.safe.title");
  const footer = (
    <div className="flex flex-col gap-5">
      <div className="flex flex-col gap-1.5">
        <div className="text-20 font-bold text-accent-solid">
          {formatPrice(safeBalanceInfo?.balance)} ₽
        </div>
        <div className="text-14 font-medium text-white/50">
          {t("business.safe.availableForWithdrawal")}
        </div>
      </div>
      <Button
        className="h-[2.75rem] w-full flex items-center gap-2.5"
        onClick={transferAllMoney}
        loading={isPending || !safeBalanceInfo}
        disabled={isPending || !safeBalanceInfo || safeBalanceInfo?.balance === 0}
      >
        <Icon name="cash" className="w-5 h-4" />
        {t("business.safe.withdrawAll")}
      </Button>
    </div>
  );

  return (
    <DeviceTabContentWrapper header={header} footer={footer} onClose={onClose}>
      <div className="flex flex-col h-full">
        <BusinessProgressBar progress={progress} />
        <div className="flex items-center gap-3.5 h-[4.6875rem] p-5 bg-dark-800/50 rounded-2xl">
          <Icon name="ruble" className="size-6 bg-accent-solid" />
          <div className=" flex flex-col gap-0.5">
            <p className="text-18 font-bold text-accent-solid">
              {formatPrice(safeBalanceInfo?.balance)} ₽
            </p>
            <p className="text-15 font-medium text-white/50">{t("business.safe.balance")}</p>
          </div>
          <div className="flex flex-col items-end gap-0.5 ml-auto">
            <p className="text-18 font-bold">{formatPrice(safeBalanceInfo?.balanceLimit)} ₽</p>
            <p className="text-15 font-medium text-white/50">{t("business.safe.capacity")}</p>
          </div>
        </div>
        <div className="flex items-center gap-2.5 py-3.5 px-1 border-b border-[#191919]">
          <Icon category="business" name="safe" className="size-4" />
          <p className="text-16 font-medium leading-none">
            {t("business.safe.levelOfImprovement")}:
          </p>
          <p className="ml-auto text-15 font-bold leading-none">2</p>
        </div>
        <div className="mt-auto">
          <p className="mb-3.5 text-11 font-bold uppercase">{t("business.safe.withdrawAmount")}:</p>
          <div className="flex gap-2">
            <TextInput
              className="w-full"
              inputClassName="h-11"
              value={inputValue}
              onChange={handleInputChange}
              placeholder={t("business.safe.enterAmount")}
              icon="₽"
              isIconRight
              maxLength={7}
            />
            <Button
              className="gap-2.5 w-55 h-11"
              onClick={transferMoney}
              loading={isPending || !safeBalanceInfo}
              disabled={isPending || !safeBalanceInfo || !inputValue}
            >
              <Icon name="cash" className="w-5 h-4" />
              {t("business.safe.withdraw")}
            </Button>
          </div>
          <p className="mt-3.5 mb-6.5 text-15 font-medium text-white/20">
            {t("business.safe.withdrawDescription")}
          </p>
        </div>
      </div>
    </DeviceTabContentWrapper>
  );
}
